import { createHash, randomBytes } from "node:crypto";
import {
  chmod,
  mkdir,
  readFile,
  rename,
  rm,
  stat,
  writeFile,
} from "node:fs/promises";
import { tmpdir } from "node:os";
import { join } from "node:path";
import {
  acquireLock,
  readLockMeta,
  releaseLock,
  runWithHeldLock,
  tryCleanupStaleLock,
  type WakeLoopLock,
} from "./lock";
import type { WakeLoopEnvPreset, WakeLoopPathResolutionInput } from "./paths";
import { resolveWakeLoopEnvPreset, runtimeDir, systemDir } from "./paths";

const DAEMON_STATE_SCHEMA = "wakeloop-daemon-install-v1";
export const DAEMON_SERVICE_RUNTIME_STATE_SCHEMA =
  "wakeloop-daemon-service-state-v1";
export const DAEMON_SERVICE_RUNTIME_MODE_ENV = "WAKELOOP_DAEMON_RUNTIME_MODE";
export const DAEMON_SERVICE_RUNTIME_DETACHED_ENV =
  "WAKELOOP_DAEMON_RUNTIME_DETACHED";
export const DAEMON_SERVICE_RUNTIME_CONTROLLER_ENV =
  "WAKELOOP_DAEMON_SERVICE_CONTROLLER";
export const DAEMON_SERVICE_RUNTIME_AUTO_START_ENV =
  "WAKELOOP_DAEMON_SERVICE_AUTO_START";
export const DAEMON_SERVICE_RUNTIME_SYSTEM_SERVICE_ENV =
  "WAKELOOP_DAEMON_RUNTIME_SYSTEM_SERVICE";
const DAEMON_TOKEN_SCHEMA = "wakeloop-daemon-token-v1";
const DAEMON_TOKEN_ROTATE_MAX_AGE_MS = 24 * 60 * 60 * 1000;
export const DAEMON_HEARTBEAT_STALE_MS = 3 * 60 * 1000;
const DAEMON_ACTIVE_BUNDLE_STATE_FILENAME = "active-bundle.json";
const DAEMON_DISPATCH_JOURNAL_FILENAME = "daemon-dispatch-journal.jsonl";
const DAEMON_LIFECYCLE_JOURNAL_FILENAME = "daemon-lifecycle-journal.jsonl";
const DAEMON_REPAIR_JOURNAL_FILENAME = "repair-journal.json";
const DAEMON_SERVICE_LOCK_PROFILE = "daemon-service";
const DAEMON_SERVICE_LOCK_KEY = "daemon-service-run";
const DAEMON_STABLE_LAUNCHER_FILENAME = "daemon-service-launcher.mjs";
const DAEMON_SERVICE_EXECUTABLE_WRAPPER_FILENAME_BY_PRESET = {
  prod: "WakeLoop Service",
  "dev-local": "WakeLoop Service Dev",
  canary: "WakeLoop Service Canary",
} as const satisfies Record<WakeLoopEnvPreset, string>;
const DAEMON_SERVICE_LEGACY_EXECUTABLE_WRAPPER_FILENAME_BY_PRESET = {
  prod: "wakeloop-service",
  "dev-local": "wakeloop-dev-service",
  canary: "wakeloop-canary-service",
} as const satisfies Record<WakeLoopEnvPreset, string>;
export const DAEMON_SERVICE_LIFECYCLE_LOCK_PROFILE =
  DAEMON_SERVICE_LOCK_PROFILE;
export const DAEMON_SERVICE_LIFECYCLE_LOCK_KEY = DAEMON_SERVICE_LOCK_KEY;
export type DaemonServiceLifecycleLock = WakeLoopLock;

export type DaemonLifecycleIntent =
  | "run_background"
  | "run_foreground"
  | "stop"
  | "autostart"
  | "status_cleanup"
  | "startup_check"
  | "start"
  | "reinstall"
  | "uninstall"
  | "upgrade_reconcile";

export type DaemonLifecycleLockMeta = {
  intent: DaemonLifecycleIntent;
  command: string;
  pid?: number;
  sessionId?: string;
  at?: string;
};

export type DaemonInstallSource = "cli.auto_check" | "daemon.command.install";
export type DaemonRunMode = "foreground" | "background";
export type DaemonServiceController =
  | "launchd"
  | "systemd"
  | "windows-service"
  | "legacy-detached"
  | "unknown";

type BaseDaemonServiceRuntimeState = {
  mode: DaemonRunMode;
  command: string;
  gatewayUrl: string | null;
  wakeLoopProfileId: string | null;
  stopRequestedAt: string | null;
  startedAt: string;
  updatedAt: string;
  heartbeatAt: string | null;
  managedBySystemService: boolean;
  serviceController: DaemonServiceController;
  autoStart: boolean;
};

export type DaemonServiceRuntimeState = BaseDaemonServiceRuntimeState & {
  v: 1;
  schema: typeof DAEMON_SERVICE_RUNTIME_STATE_SCHEMA;
  status: "running";
  pid: number;
};

export type DaemonServiceRuntimeStatus = {
  status: "running" | "stale" | "not_running" | "unknown";
  mode: DaemonRunMode | null;
  pid: number | null;
  startedAt: string | null;
  updatedAt: string | null;
  managedBySystemService: boolean;
  serviceController: DaemonServiceRuntimeState["serviceController"];
  autoStart: boolean;
  reason?: string;
};

export type DaemonServiceRuntimeMetadataInput = {
  managedBySystemService?: boolean;
  serviceController?: string;
  autoStart?: boolean;
  env?: NodeJS.ProcessEnv;
};

export type DaemonServiceRuntimeMetadata = {
  managedBySystemService: boolean;
  serviceController: DaemonServiceController;
  autoStart: boolean;
};

export type DaemonInstallState = {
  v: 1;
  schema: typeof DAEMON_STATE_SCHEMA;
  daemonVersion: string;
  installSource: DaemonInstallSource;
  installedAt: string;
  updatedAt: string;
};

export type DaemonTokenState = {
  v: 1;
  schema: typeof DAEMON_TOKEN_SCHEMA;
  token: string;
  issuedAt: string;
  updatedAt: string;
};

export type DaemonInstallResult = {
  status: "installed" | "already_installed";
  state: DaemonInstallState;
};

export type DaemonUninstallResult = {
  removed: boolean;
};

export type DaemonStatus =
  | {
      installed: true;
      state: DaemonInstallState;
    }
  | {
      installed: false;
      state: null;
    };

export async function getDaemonStatus(
  pathInput: WakeLoopPathResolutionInput = {}
): Promise<DaemonStatus> {
  const [state, runtimeExists] = await Promise.all([
    readDaemonInstallState(pathInput),
    pathExists(daemonRuntimeRootPath(pathInput)),
  ]);
  if (state && runtimeExists) {
    return {
      installed: true,
      state,
    };
  }
  return {
    installed: false,
    state: null,
  };
}

export async function getDaemonRuntimeStatus(
  pathInput: WakeLoopPathResolutionInput = {}
): Promise<DaemonServiceRuntimeStatus> {
  const state = await readDaemonServiceRuntimeState(pathInput);
  if (!state) {
    return {
      status: "not_running",
      mode: null,
      pid: null,
      startedAt: null,
      updatedAt: null,
      managedBySystemService: false,
      serviceController: "unknown",
      autoStart: false,
    };
  }
  if (!Number.isInteger(state.pid) || state.pid <= 0) {
    return {
      status: "stale",
      mode: state.mode,
      pid: state.pid,
      startedAt: state.startedAt,
      updatedAt: state.updatedAt,
      managedBySystemService: state.managedBySystemService,
      serviceController: state.serviceController,
      autoStart: state.autoStart,
      reason: "invalid_pid",
    };
  }
  if (!isPidAlive(state.pid)) {
    return {
      status: "stale",
      mode: state.mode,
      pid: state.pid,
      startedAt: state.startedAt,
      updatedAt: state.updatedAt,
      managedBySystemService: state.managedBySystemService,
      serviceController: state.serviceController,
      autoStart: state.autoStart,
      reason: "process_not_alive",
    };
  }
  if (isDaemonServiceRuntimeHeartbeatStale(state)) {
    return {
      status: "stale",
      mode: state.mode,
      pid: state.pid,
      startedAt: state.startedAt,
      updatedAt: state.updatedAt,
      managedBySystemService: state.managedBySystemService,
      serviceController: state.serviceController,
      autoStart: state.autoStart,
      reason: "heartbeat_stale",
    };
  }
  return {
    status: "running",
    mode: state.mode,
    pid: state.pid,
    startedAt: state.startedAt,
    updatedAt: state.updatedAt,
    managedBySystemService: state.managedBySystemService,
    serviceController: state.serviceController,
    autoStart: state.autoStart,
  };
}

export async function readDaemonServiceRuntimeSnapshot(
  pathInput: WakeLoopPathResolutionInput = {}
): Promise<DaemonServiceRuntimeState | null> {
  return await readDaemonServiceRuntimeState(pathInput);
}

export function resolveDaemonServiceRuntimeMetadata(
  input: DaemonServiceRuntimeMetadataInput = {}
): DaemonServiceRuntimeMetadata {
  const env = input.env ?? process.env;
  const managedBySystemService =
    input.managedBySystemService === true ||
    env[DAEMON_SERVICE_RUNTIME_SYSTEM_SERVICE_ENV] === "1";
  const serviceControllerInput =
    typeof input.serviceController === "string" &&
    input.serviceController.length > 0
      ? input.serviceController
      : env[DAEMON_SERVICE_RUNTIME_CONTROLLER_ENV];
  const autoStart =
    input.autoStart === true ||
    env[DAEMON_SERVICE_RUNTIME_AUTO_START_ENV] === "1";

  return {
    managedBySystemService,
    serviceController: parseDaemonServiceController(serviceControllerInput),
    autoStart,
  };
}

export async function setDaemonServiceRuntimeState(
  state: DaemonServiceRuntimeState,
  pathInput: WakeLoopPathResolutionInput = {}
): Promise<void> {
  await writeDaemonServiceRuntimeState(state, pathInput);
}

export async function clearDaemonServiceRuntimeState(
  pathInput: WakeLoopPathResolutionInput = {}
): Promise<void> {
  const { clearDaemonRuntimeLease } = await import("./daemon-runtime-lease");
  await Promise.all([
    rm(daemonServiceRuntimeStatePath(pathInput), { force: true }),
    clearDaemonRuntimeLease(pathInput),
  ]);
}

export async function withDaemonServiceRunLock<T>(
  callback: () => Promise<T>,
  meta: DaemonLifecycleLockMeta = {
    intent: "run_background",
    command: "wakeloop service start",
  }
): Promise<T> {
  return await withDaemonServiceLifecycleLock(meta, callback);
}

export async function acquireDaemonServiceLifecycleLock(
  meta: DaemonLifecycleLockMeta = {
    intent: "run_background",
    command: "wakeloop service start",
  }
): Promise<DaemonServiceLifecycleLock> {
  return await acquireLock({
    wakeLoopProfileId: DAEMON_SERVICE_LIFECYCLE_LOCK_PROFILE,
    key: DAEMON_SERVICE_LIFECYCLE_LOCK_KEY,
    meta,
  });
}

export async function releaseDaemonServiceLifecycleLock(
  lock: DaemonServiceLifecycleLock
): Promise<void> {
  await releaseLock(lock);
}

export async function withDaemonServiceStopLock<T>(
  callback: () => Promise<T>,
  meta: DaemonLifecycleLockMeta = {
    intent: "stop",
    command: "wakeloop service stop",
  }
): Promise<T> {
  return await withDaemonServiceLifecycleLock(meta, callback);
}

export async function withDaemonServiceReconcileLock<T>(
  callback: () => Promise<T>,
  meta: DaemonLifecycleLockMeta = {
    intent: "upgrade_reconcile",
    command: "wakeloop service update",
  }
): Promise<T> {
  return await withDaemonServiceLifecycleLock(meta, callback);
}

export async function withDaemonServiceLifecycleLock<T>(
  meta: DaemonLifecycleLockMeta,
  callback: () => Promise<T>
): Promise<T> {
  const lock = await acquireDaemonServiceLifecycleLock(meta);
  return await runWithHeldLock({
    lock,
    run: callback,
    releaseContext: {
      command: meta.command,
      component: "daemon_service_lifecycle",
      intent: meta.intent,
      lockKey: DAEMON_SERVICE_LIFECYCLE_LOCK_KEY,
      wakeLoopProfileId: DAEMON_SERVICE_LIFECYCLE_LOCK_PROFILE,
    },
  });
}

export async function readDaemonServiceLifecycleLockMeta(): Promise<DaemonLifecycleLockMeta | null> {
  const meta = await readLockMeta({
    wakeLoopProfileId: DAEMON_SERVICE_LIFECYCLE_LOCK_PROFILE,
    key: DAEMON_SERVICE_LIFECYCLE_LOCK_KEY,
  });
  if (!meta) {
    return null;
  }
  const intent =
    typeof meta.intent === "string"
      ? normalizeDaemonLifecycleIntent(meta.intent)
      : null;
  const command = typeof meta.command === "string" ? meta.command.trim() : "";
  if (!intent || command.length === 0) {
    return null;
  }
  const pid = typeof meta.pid === "number" ? meta.pid : undefined;
  const sessionId =
    typeof meta.sessionId === "string" && meta.sessionId.trim().length > 0
      ? meta.sessionId.trim()
      : undefined;
  const at =
    typeof meta.at === "string" && meta.at.trim().length > 0
      ? meta.at.trim()
      : undefined;
  return {
    intent,
    command,
    ...(typeof pid === "number" ? { pid } : {}),
    ...(sessionId ? { sessionId } : {}),
    ...(at ? { at } : {}),
  };
}

export async function readDaemonServiceLifecycleLockMetaWithCleanup(): Promise<DaemonLifecycleLockMeta | null> {
  await tryCleanupStaleLock({
    wakeLoopProfileId: DAEMON_SERVICE_LIFECYCLE_LOCK_PROFILE,
    key: DAEMON_SERVICE_LIFECYCLE_LOCK_KEY,
  });
  return await readDaemonServiceLifecycleLockMeta();
}

export async function installDaemon(input: {
  daemonVersion: string;
  source: DaemonInstallSource;
}): Promise<DaemonInstallResult> {
  const existingState = await readDaemonInstallState();
  const runtimeExists = await pathExists(daemonRuntimeRootPath());
  if (
    existingState &&
    runtimeExists &&
    existingState.daemonVersion === input.daemonVersion
  ) {
    await ensureDaemonTokenState();
    return {
      status: "already_installed",
      state: existingState,
    };
  }

  await ensureDaemonRuntimeLayout();
  const now = new Date().toISOString();
  const nextState: DaemonInstallState = {
    v: 1,
    schema: DAEMON_STATE_SCHEMA,
    daemonVersion: input.daemonVersion,
    installSource: input.source,
    installedAt: existingState?.installedAt ?? now,
    updatedAt: now,
  };
  await writeDaemonInstallState(nextState);
  await ensureDaemonTokenState();

  return {
    status: "installed",
    state: nextState,
  };
}

export async function uninstallDaemon(
  pathInput: WakeLoopPathResolutionInput = {}
): Promise<DaemonUninstallResult> {
  const [hasState, hasRuntimeDir, hasToken] = await Promise.all([
    pathExists(daemonStatePath(pathInput)),
    pathExists(daemonRuntimeRootPath(pathInput)),
    pathExists(daemonTokenPath(pathInput)),
  ]);

  await Promise.all([
    rm(daemonStatePath(pathInput), { force: true }),
    rm(daemonTokenPath(pathInput), { force: true }),
    rm(daemonServiceRuntimeStatePath(pathInput), { force: true }),
    rm(daemonRuntimeRootPath(pathInput), { recursive: true, force: true }),
  ]);

  return {
    removed: hasState || hasRuntimeDir || hasToken,
  };
}

export async function refreshDaemonTokenLifecycle(): Promise<{
  tokenUpdatedAt: string;
} | null> {
  const status = await getDaemonStatus();
  if (!status.installed) {
    return null;
  }

  const tokenState = await ensureDaemonTokenState();
  return {
    tokenUpdatedAt: tokenState.updatedAt,
  };
}

export function daemonStatePath(
  pathInput: WakeLoopPathResolutionInput = {}
): string {
  return join(systemDir(pathInput), "daemon-install.json");
}

export function daemonRuntimeRootPath(
  pathInput: WakeLoopPathResolutionInput = {}
): string {
  return join(runtimeDir(pathInput), "daemon");
}

export function daemonServiceRuntimeStatePath(
  pathInput: WakeLoopPathResolutionInput = {}
): string {
  return join(daemonRuntimeRootPath(pathInput), "service-runtime.json");
}

export function daemonRuntimeDataPath(
  pathInput: WakeLoopPathResolutionInput = {}
): string {
  return join(daemonRuntimeRootPath(pathInput), "data");
}

export function daemonRuntimeBundlesPath(
  pathInput: WakeLoopPathResolutionInput = {}
): string {
  return join(daemonRuntimeRootPath(pathInput), "bundles");
}

export function daemonRuntimeStateRootPath(
  pathInput: WakeLoopPathResolutionInput = {}
): string {
  return join(daemonRuntimeRootPath(pathInput), "state");
}

export function daemonLifecycleJournalPath(
  pathInput: WakeLoopPathResolutionInput = {}
): string {
  return join(
    daemonRuntimeRootPath(pathInput),
    "logs",
    DAEMON_LIFECYCLE_JOURNAL_FILENAME
  );
}

export function daemonDispatchJournalPath(
  pathInput: WakeLoopPathResolutionInput = {}
): string {
  return join(
    daemonRuntimeRootPath(pathInput),
    "logs",
    DAEMON_DISPATCH_JOURNAL_FILENAME
  );
}

export function daemonServiceActiveBundleStatePath(
  pathInput: WakeLoopPathResolutionInput = {}
): string {
  return join(
    daemonRuntimeStateRootPath(pathInput),
    DAEMON_ACTIVE_BUNDLE_STATE_FILENAME
  );
}

export function daemonServiceRepairJournalPath(
  pathInput: WakeLoopPathResolutionInput = {}
): string {
  return join(
    daemonRuntimeStateRootPath(pathInput),
    DAEMON_REPAIR_JOURNAL_FILENAME
  );
}

export function daemonRuntimeServiceManagerPath(
  pathInput: WakeLoopPathResolutionInput = {}
): string {
  return join(daemonRuntimeRootPath(pathInput), "service-manager");
}

export function daemonServiceStableLauncherPath(
  pathInput: WakeLoopPathResolutionInput = {}
): string {
  return join(
    daemonRuntimeServiceManagerPath(pathInput),
    DAEMON_STABLE_LAUNCHER_FILENAME
  );
}

export function daemonServiceStableExecutableWrapperPath(
  pathInput: WakeLoopPathResolutionInput = {}
): string {
  const preset = resolveWakeLoopEnvPreset(pathInput);
  return join(
    daemonRuntimeServiceManagerPath(pathInput),
    DAEMON_SERVICE_EXECUTABLE_WRAPPER_FILENAME_BY_PRESET[preset]
  );
}

export function daemonServiceLegacyExecutableWrapperPath(
  pathInput: WakeLoopPathResolutionInput = {}
): string {
  const preset = resolveWakeLoopEnvPreset(pathInput);
  return join(
    daemonRuntimeServiceManagerPath(pathInput),
    DAEMON_SERVICE_LEGACY_EXECUTABLE_WRAPPER_FILENAME_BY_PRESET[preset]
  );
}

export function daemonServiceRecognizedExecutableWrapperPaths(
  pathInput: WakeLoopPathResolutionInput = {}
): string[] {
  return [
    daemonServiceStableExecutableWrapperPath(pathInput),
    daemonServiceLegacyExecutableWrapperPath(pathInput),
  ];
}

export function daemonTokenPath(
  pathInput: WakeLoopPathResolutionInput = {}
): string {
  return join(systemDir(pathInput), "daemon-token.json");
}

export async function restoreDaemonInstallStateSnapshot(
  snapshot: DaemonInstallState | null,
  pathInput: WakeLoopPathResolutionInput = {}
): Promise<void> {
  if (!snapshot) {
    await rm(daemonStatePath(pathInput), { force: true });
    return;
  }
  await writeDaemonInstallState(snapshot, pathInput);
}

export function resolveDaemonControlPlaneEndpoint(
  pathInput: WakeLoopPathResolutionInput = {}
): string {
  const runtimeRoot = daemonRuntimeRootPath(pathInput);
  const suffix = createHash("sha256")
    .update(runtimeRoot)
    .digest("hex")
    .slice(0, 24);
  if (process.platform === "win32") {
    return `\\\\.\\pipe\\wakeloop-daemon-control-${suffix}`;
  }
  return join(tmpdir(), `wakeloop-daemon-control-${suffix}.sock`);
}

export async function ensureDaemonControlPlaneToken(
  pathInput: WakeLoopPathResolutionInput = {}
): Promise<string> {
  const state = await ensureDaemonTokenState(pathInput);
  return state.token;
}

export async function readDaemonControlPlaneToken(
  pathInput: WakeLoopPathResolutionInput = {}
): Promise<string | null> {
  const state = await readDaemonTokenState(pathInput);
  return state?.token ?? null;
}

function isPidAlive(pid: number): boolean {
  try {
    process.kill(pid, 0);
    return true;
  } catch (error) {
    return !(
      error instanceof Error &&
      (error as NodeJS.ErrnoException).code === "ESRCH"
    );
  }
}

async function readDaemonServiceRuntimeState(
  pathInput: WakeLoopPathResolutionInput = {}
): Promise<DaemonServiceRuntimeState | null> {
  const raw = await readFile(
    daemonServiceRuntimeStatePath(pathInput),
    "utf8"
  ).catch((error) => {
    if (
      error &&
      typeof error === "object" &&
      "code" in error &&
      error.code === "ENOENT"
    ) {
      return null;
    }
    throw error;
  });
  if (raw === null) {
    return null;
  }
  return parseDaemonServiceRuntimeState(raw);
}

function parseDaemonServiceRuntimeState(
  raw: string
): DaemonServiceRuntimeState | null {
  try {
    const parsed = JSON.parse(raw);
    if (!isDaemonServiceRuntimeStateObject(parsed)) {
      return null;
    }

    return {
      v: 1,
      schema: DAEMON_SERVICE_RUNTIME_STATE_SCHEMA,
      status: "running",
      pid: parsed.pid,
      mode: parsed.mode,
      command: parsed.command,
      gatewayUrl: parseNullableString(parsed.gatewayUrl),
      wakeLoopProfileId: resolveStoredDaemonServiceWakeLoopProfileId(parsed),
      stopRequestedAt: parseNullableString(parsed.stopRequestedAt),
      heartbeatAt: parseNullableString(parsed.heartbeatAt),
      startedAt: parsed.startedAt,
      updatedAt: parsed.updatedAt,
      managedBySystemService: parsed.managedBySystemService === true,
      serviceController: parseDaemonServiceController(parsed.serviceController),
      autoStart: parsed.autoStart === true,
    };
  } catch {
    return null;
  }
}

function isDaemonServiceRuntimeStateObject(value: unknown): value is {
  v: 1;
  schema: string;
  status: "running";
  pid: number;
  mode: DaemonRunMode;
  command: string;
  gatewayUrl: string | null;
  wakeLoopProfileId?: string | null;
  profile?: string | null;
  stopRequestedAt: string | null;
  heartbeatAt: string | null;
  startedAt: string;
  updatedAt: string;
  managedBySystemService?: boolean;
  serviceController?: string;
  autoStart?: boolean;
} {
  if (!value || typeof value !== "object") {
    return false;
  }
  const candidate = value as Record<string, unknown>;
  return (
    candidate.v === 1 &&
    candidate.schema === DAEMON_SERVICE_RUNTIME_STATE_SCHEMA &&
    candidate.status === "running" &&
    typeof candidate.pid === "number" &&
    candidate.pid > 0 &&
    (candidate.mode === "foreground" || candidate.mode === "background") &&
    typeof candidate.command === "string" &&
    candidate.command.length > 0 &&
    isValidNullableString(candidate.gatewayUrl) &&
    isValidOptionalNullableString(candidate.wakeLoopProfileId) &&
    isValidOptionalNullableString(candidate.profile) &&
    isValidNullableString(candidate.stopRequestedAt) &&
    isValidNullableString(candidate.heartbeatAt) &&
    typeof candidate.startedAt === "string" &&
    candidate.startedAt.length > 0 &&
    typeof candidate.updatedAt === "string" &&
    candidate.updatedAt.length > 0
  );
}

export function parseDaemonServiceController(
  value: unknown
): DaemonServiceController {
  if (
    value === "launchd" ||
    value === "systemd" ||
    value === "windows-service" ||
    value === "legacy-detached" ||
    value === "unknown"
  ) {
    return value;
  }
  return "unknown";
}

function isValidNullableString(value: unknown): value is string | null {
  return value === null || typeof value === "string";
}

function isValidOptionalNullableString(
  value: unknown
): value is string | null | undefined {
  return value === undefined || value === null || typeof value === "string";
}

function parseNullableString(value: unknown): string | null {
  if (value === null) {
    return null;
  }
  if (typeof value !== "string") {
    return null;
  }
  return value;
}

function resolveStoredDaemonServiceWakeLoopProfileId(input: {
  wakeLoopProfileId?: unknown;
  profile?: unknown;
}): string | null {
  const wakeLoopProfileId = parseNullableString(input.wakeLoopProfileId);
  const legacyProfile = parseNullableString(input.profile);
  if (
    wakeLoopProfileId &&
    legacyProfile &&
    wakeLoopProfileId !== legacyProfile
  ) {
    throw new Error("conflicting wakeLoopProfileId values");
  }
  return wakeLoopProfileId ?? legacyProfile;
}

export function isDaemonServiceRuntimeHeartbeatStale(
  state: DaemonServiceRuntimeState
): boolean {
  const latestSignalAt = Math.max(
    parseIsoTimestampOrZero(state.heartbeatAt),
    parseIsoTimestampOrZero(state.updatedAt)
  );
  if (latestSignalAt <= 0) {
    return false;
  }
  return Date.now() - latestSignalAt > DAEMON_HEARTBEAT_STALE_MS;
}

function parseIsoTimestampOrZero(value: string | null): number {
  if (!value) {
    return 0;
  }
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

async function writeDaemonServiceRuntimeState(
  state: DaemonServiceRuntimeState,
  pathInput: WakeLoopPathResolutionInput = {}
): Promise<void> {
  const path = daemonServiceRuntimeStatePath(pathInput);
  const runtimeRootPath = daemonRuntimeRootPath(pathInput);
  await mkdir(runtimeRootPath, { recursive: true });
  const tempPath = `${path}.tmp-${process.pid}-${Date.now()}`;
  let shouldCleanupTemp = true;

  try {
    await writeFile(tempPath, `${JSON.stringify(state, null, 2)}\n`, {
      encoding: "utf8",
      mode: 0o600,
    });
    await rename(tempPath, path);
    await chmod(path, 0o600);
    shouldCleanupTemp = false;
  } finally {
    if (shouldCleanupTemp) {
      await rm(tempPath, { force: true }).catch(() => undefined);
    }
  }
}

async function ensureDaemonRuntimeLayout(): Promise<void> {
  await Promise.all([
    mkdir(systemDir(), { recursive: true }),
    mkdir(join(daemonRuntimeRootPath(), "bin"), { recursive: true }),
    mkdir(daemonRuntimeBundlesPath(), { recursive: true }),
    mkdir(join(daemonRuntimeRootPath(), "data"), { recursive: true }),
    mkdir(join(daemonRuntimeRootPath(), "logs"), { recursive: true }),
    mkdir(daemonRuntimeStateRootPath(), { recursive: true }),
    mkdir(daemonRuntimeServiceManagerPath(), { recursive: true }),
  ]);
}

function normalizeDaemonLifecycleIntent(
  value: string
): DaemonLifecycleIntent | null {
  switch (value) {
    case "run_background":
    case "run_foreground":
    case "stop":
    case "autostart":
    case "status_cleanup":
    case "startup_check":
    case "start":
    case "reinstall":
    case "uninstall":
    case "upgrade_reconcile":
      return value;
    default:
      return null;
  }
}

async function ensureDaemonTokenState(
  pathInput: WakeLoopPathResolutionInput = {}
): Promise<DaemonTokenState> {
  const current = await readDaemonTokenState(pathInput);
  if (current && !isDaemonTokenExpired(current)) {
    await chmod(daemonTokenPath(pathInput), 0o600).catch(() => undefined);
    return current;
  }

  const nowIso = new Date().toISOString();
  const next: DaemonTokenState = {
    v: 1,
    schema: DAEMON_TOKEN_SCHEMA,
    token: createDaemonTokenValue(),
    issuedAt: nowIso,
    updatedAt: nowIso,
  };
  await writeDaemonTokenState(next, pathInput);
  return next;
}

async function readDaemonInstallState(
  pathInput: WakeLoopPathResolutionInput = {}
): Promise<DaemonInstallState | null> {
  const raw = await readFile(daemonStatePath(pathInput), "utf8").catch(
    (error) => {
      if (
        error &&
        typeof error === "object" &&
        "code" in error &&
        error.code === "ENOENT"
      ) {
        return null;
      }
      throw error;
    }
  );
  if (raw === null) {
    return null;
  }
  return parseDaemonInstallState(raw);
}

function parseDaemonInstallState(raw: string): DaemonInstallState | null {
  try {
    const parsed = JSON.parse(raw) as Partial<DaemonInstallState>;
    if (parsed.v !== 1) {
      return null;
    }
    if (parsed.schema !== DAEMON_STATE_SCHEMA) {
      return null;
    }
    if (typeof parsed.daemonVersion !== "string" || !parsed.daemonVersion) {
      return null;
    }
    if (!isDaemonInstallSource(parsed.installSource)) {
      return null;
    }
    if (typeof parsed.installedAt !== "string" || !parsed.installedAt) {
      return null;
    }
    if (typeof parsed.updatedAt !== "string" || !parsed.updatedAt) {
      return null;
    }
    return {
      v: 1,
      schema: DAEMON_STATE_SCHEMA,
      daemonVersion: parsed.daemonVersion,
      installSource: parsed.installSource,
      installedAt: parsed.installedAt,
      updatedAt: parsed.updatedAt,
    };
  } catch {
    return null;
  }
}

function isDaemonInstallSource(value: unknown): value is DaemonInstallSource {
  return value === "cli.auto_check" || value === "daemon.command.install";
}

async function writeDaemonInstallState(
  state: DaemonInstallState,
  pathInput: WakeLoopPathResolutionInput = {}
): Promise<void> {
  const path = daemonStatePath(pathInput);
  await mkdir(systemDir(pathInput), { recursive: true });
  const tempPath = `${path}.tmp-${process.pid}-${Date.now()}`;
  let shouldCleanupTemp = true;

  try {
    await writeFile(tempPath, `${JSON.stringify(state, null, 2)}\n`, "utf8");
    await rename(tempPath, path);
    shouldCleanupTemp = false;
  } finally {
    if (shouldCleanupTemp) {
      await rm(tempPath, { force: true }).catch(() => undefined);
    }
  }
}

async function readDaemonTokenState(
  pathInput: WakeLoopPathResolutionInput = {}
): Promise<DaemonTokenState | null> {
  const raw = await readFile(daemonTokenPath(pathInput), "utf8").catch(
    (error) => {
      if (
        error &&
        typeof error === "object" &&
        "code" in error &&
        error.code === "ENOENT"
      ) {
        return null;
      }
      throw error;
    }
  );
  if (raw === null) {
    return null;
  }
  return parseDaemonTokenState(raw);
}

function parseDaemonTokenState(raw: string): DaemonTokenState | null {
  try {
    const parsed = JSON.parse(raw) as Partial<DaemonTokenState>;
    if (parsed.v !== 1 || parsed.schema !== DAEMON_TOKEN_SCHEMA) {
      return null;
    }
    if (typeof parsed.token !== "string" || parsed.token.length < 16) {
      return null;
    }
    if (typeof parsed.issuedAt !== "string" || !parsed.issuedAt) {
      return null;
    }
    if (typeof parsed.updatedAt !== "string" || !parsed.updatedAt) {
      return null;
    }
    return {
      v: 1,
      schema: DAEMON_TOKEN_SCHEMA,
      token: parsed.token,
      issuedAt: parsed.issuedAt,
      updatedAt: parsed.updatedAt,
    };
  } catch {
    return null;
  }
}

async function writeDaemonTokenState(
  state: DaemonTokenState,
  pathInput: WakeLoopPathResolutionInput = {}
): Promise<void> {
  const path = daemonTokenPath(pathInput);
  await mkdir(systemDir(pathInput), { recursive: true });
  const tempPath = `${path}.tmp-${process.pid}-${Date.now()}`;
  let shouldCleanupTemp = true;

  try {
    await writeFile(tempPath, `${JSON.stringify(state, null, 2)}\n`, {
      encoding: "utf8",
      mode: 0o600,
    });
    await rename(tempPath, path);
    await chmod(path, 0o600);
    shouldCleanupTemp = false;
  } finally {
    if (shouldCleanupTemp) {
      await rm(tempPath, { force: true }).catch(() => undefined);
    }
  }
}

function isDaemonTokenExpired(state: DaemonTokenState): boolean {
  const updatedAtMs = Date.parse(state.updatedAt);
  if (!Number.isFinite(updatedAtMs)) {
    return true;
  }
  return Date.now() - updatedAtMs >= DAEMON_TOKEN_ROTATE_MAX_AGE_MS;
}

function createDaemonTokenValue(): string {
  return randomBytes(32).toString("base64url");
}

async function pathExists(path: string): Promise<boolean> {
  try {
    await stat(path);
    return true;
  } catch (error) {
    if (
      error &&
      typeof error === "object" &&
      "code" in error &&
      error.code === "ENOENT"
    ) {
      return false;
    }
    throw error;
  }
}
